/** Keys that would write into a prototype instead of the object itself. */
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Checks whether a value is a plain object: an object literal, `Object.create(null)`, or a parsed JSON object.
 *
 * Works across realms, so objects from an iframe, a `vm` context or `structuredClone` under Jest still count, unlike an
 * `Object.prototype` identity check.
 *
 * @param value - The value to check
 * @returns `true` if the value is a plain object, otherwise `false`.
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (Object.prototype.toString.call(value) !== '[object Object]') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === null || Object.getPrototypeOf(prototype) === null;
}

/**
 * Deep-merges `source` into `target`, mutating and returning `target`.
 *
 * Plain objects are merged key by key. Every other value, arrays included, replaces the target's value. `undefined`
 * source values are skipped, so they never clear a target value. `__proto__`, `constructor` and `prototype` keys are
 * ignored to prevent prototype pollution.
 *
 * @param target - The object to merge into
 * @param source - The object whose values are merged in
 * @returns `target`, after the merge.
 */
export function deepMerge<T extends object>(target: T, source: object): T {
  const merged = target as Record<string, unknown>;
  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined || UNSAFE_KEYS.has(key)) {
      return;
    }

    merged[key] = isPlainObject(value) ? deepMerge(isPlainObject(merged[key]) ? merged[key] : {}, value) : value;
  });
  return target;
}
